import { data } from "react-router"

import { useLoaderData, useSearchParams } from "react-router"
import HomepageProducts from "#app/components/home-page-products"
import LatestBlogPosts from "#app/components/latest-blog-posts"
import HandmadeQualityArts from "#app/components/logos/handmade-quality-arts.tsx"
import {
	TypographyH1,
	TypographyH2,
	TypographyP,
} from "#app/components/ui/typography.tsx"
import { Button } from "#app/components/ui/button"
import { Icon } from "#app/components/ui/icon"
import { APP_NAME } from "#app/utils/constants"
import { prisma } from "#app/utils/db.server.ts"
import { generateMeta } from "#app/utils/meta.js"
import { useOptionalUser } from "#app/utils/user"
import { useWishlist } from "#app/utils/wishlist-context"

export const meta = generateMeta({
	title: `${APP_NAME} - Patchwork Hecho a Mano`,
	description:
		"Descubre un mundo de productos exquisitos de patchwork hechos a mano. Explora la mezcla perfecta de arte y calidad en nuestros productos únicos.",
})

export const links = () => [
	// Preload LCP image immediately without media queries for fastest discovery
	// Using responsive preload with imagesrcset for browser to choose appropriate size
	{
		rel: "preload",
		as: "image",
		imageSrcSet:
			"/logos/handmade-quality-arts-400.webp 400w, /logos/handmade-quality-arts-600.webp 600w, /logos/handmade-quality-arts-800.webp 800w",
		imageSizes:
			"(max-width: 640px) 80vw, (max-width: 768px) 280px, (max-width: 1024px) 320px, (max-width: 1280px) 400px, 500px",
	},
]

export async function loader() {
	const [products, recentPosts] = await Promise.all([
		prisma.product.findMany({
			select: {
				id: true,
				name: true,
				price: true,
				handle: true,
				isUnique: true,
				images: {
					select: {
						id: true,
						altText: true,
					},
					take: 1,
				},
				variants: {
					where: {
						enabled: true,
					},
					select: {
						id: true,
						price: true,
					},
				},
			},
			where: {
				published: true,
			},
		}),
		prisma.post.findMany({
			where: { published: true },
			select: {
				id: true,
				title: true,
				slug: true,
				excerpt: true,
				publishedAt: true,
				images: {
					select: { id: true, altText: true },
					take: 1,
				},
			},
			orderBy: { publishedAt: "desc" },
			take: 3,
		}),
	])

	const posts = recentPosts.map(post => ({
		...post,
		imageId: post.images[0]?.id ?? null,
	}))

	return data({
		products,
		posts,
	})
}

export default function Index() {
	const { products, posts } = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const { wishlist } = useWishlist()
	const [searchParams, setSearchParams] = useSearchParams()

	const showFavorites = searchParams.get("favorites") === "true"

	const displayProducts = showFavorites
		? products.filter(p => wishlist.includes(p.id))
		: products

	const toggleFavorites = () => {
		const newParams = new URLSearchParams(searchParams)
		if (showFavorites) {
			newParams.delete("favorites")
		} else {
			newParams.set("favorites", "true")
		}
		setSearchParams(newParams)
	}

	return (
		<main className="container h-full overflow-x-hidden">
			<div className="grid place-items-center gap-8 py-12 md:grid-cols-2 md:gap-12 md:py-16 xl:gap-24">
				<div className="flex w-full min-w-0 max-w-md flex-col items-center justify-center gap-4 text-center md:max-w-none md:items-start md:text-left xl:max-w-[600px]">
					<TypographyH1 data-heading className="md:text-2xl lg:text-3xl">
						El Emporio Digital del Patchwork
					</TypographyH1>
					<TypographyP data-paragraph className="md:text-lg">
						Explora un mundo de productos exquisitos de patchwork hechos a mano
						en {APP_NAME}. Descubre la mezcla perfecta de arte y calidad en
						nuestros productos.
					</TypographyP>
					<TypographyP data-paragraph className="md:text-lg">
						Experimente el encanto de diseños únicos, colores vibrantes y
						costuras intrincadas, todos creados con pasión y dedicación.
						Transforma tu espacio con la calidez y belleza del patchwork hecho a
						mano, donde cada pieza cuenta una historia. Compre ahora y aporte un
						toque de elegancia artística a su vida.
					</TypographyP>
				</div>
				<HandmadeQualityArts className="w-full max-w-[280px] md:max-w-[320px] lg:max-w-[400px] xl:max-w-lg" />
			</div>

			<div>
				<div className="mb-6 flex items-center justify-between">
					<TypographyH2>
						{showFavorites ? "Mis favoritos" : "Todos nuestros productos"}
					</TypographyH2>
					{wishlist.length > 0 && (
						<Button
							variant={showFavorites ? "secondary" : "outline"}
							onClick={toggleFavorites}
							className="flex items-center gap-2"
						>
							<Icon name={showFavorites ? "heart-filled" : "heart"} />
							{showFavorites ? "Ver todos" : "Solo favoritos"}
						</Button>
					)}
				</div>

				<div className="relative my-[50px]">
					{showFavorites && displayProducts.length === 0 ? (
						<div className="flex flex-col items-center gap-4 py-12">
							<Icon name="heart" className="h-12 w-12 text-muted-foreground" />
							<TypographyP className="text-muted-foreground">
								No tienes productos favoritos aún. ¡Explora y añade tus
								favoritos!
							</TypographyP>
							<Button onClick={toggleFavorites}>Ver todos los productos</Button>
						</div>
					) : (
						<HomepageProducts products={displayProducts} />
					)}
				</div>
			</div>

			{posts.length > 0 && <LatestBlogPosts posts={posts} />}
		</main>
	)
}
